'use client';

import { usePathname } from 'next/navigation';

import type { TabsProps } from '@mui/material/Tabs';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { tss } from 'tss-react/mui';

import { Skeleton } from '@mui/material';

import { forwardRef } from 'react';

import { nonFalsey } from '@/utils/nonFalsey';

import type { LinkProps } from './Or2Link';
import { Or2Link } from './Or2Link';

export interface Or2Tab {
  activePath?: string;
  path: string;
  name: string;
  props?: Record<string, string>;
  onClick?: () => void;
  url?: string;
}

export interface Or2TabProps extends TabsProps {
  tabs: null | undefined | Or2Tab[];
  fullWidth?: boolean;
  loading?: boolean;
  thickLoader?: boolean;
  scroll?: boolean;
}

const useStyles = tss.create(() => ({
  tab: {
    '&:hover': {
      textDecoration: 'none',
    },
    '&:focus': {
      outline: 'none',
      textDecoration: 'none',
    },
    '&:active': {
      textDecoration: 'none',
    },
  },
}));

const Or2LinkWithNoScroll = forwardRef<HTMLAnchorElement, LinkProps>(
  function Or2LinkWithProps(props, ref) {
    return <Or2Link ref={ref} {...props} scroll={false} />;
  },
);

export const Or2Tabs = ({
  tabs,
  fullWidth = false,
  loading = false,
  thickLoader = true,
  scroll = true,
  ...other
}: Or2TabProps) => {
  const pathname = usePathname();

  // Active tab has largest matching prefix with current path
  const matchingTabs = tabs
    ?.map(({ activePath }) => {
      const match = pathname.includes(activePath ?? '');
      if (match) {
        return String(activePath);
      }
      return false;
    })
    .filter(nonFalsey);

  const activeTabValue = matchingTabs?.length
    ? matchingTabs?.reduce((a, b) => (a?.length > b?.length ? a : b))
    : undefined;

  const { classes } = useStyles();

  const LinkComponent = scroll ? Or2Link : Or2LinkWithNoScroll;

  return loading ? (
    <Skeleton
      sx={thickLoader ? { transform: 'none' } : undefined}
      height="100%"
      width="100%"
      animation="wave"
    >
      <Tab />
    </Skeleton>
  ) : (
    <Tabs
      allowScrollButtonsMobile
      indicatorColor="primary"
      textColor="primary"
      value={activeTabValue || false}
      variant={'scrollable'}
      scrollButtons="auto"
      {...other}
    >
      {(tabs || []).map((tab) => {
        return tab.url ? (
          <Tab
            key={tab.name}
            label={tab.name}
            className={classes.tab}
            href={tab.url}
            component={LinkComponent}
            underline="none"
            onClick={tab.onClick}
            sx={
              fullWidth
                ? { maxWidth: 'none', minWidth: 'fit-content', flex: 1 }
                : undefined
            }
            {...tab.props}
          />
        ) : (
          <Tab
            label={tab.name}
            key={tab.name}
            href={tab.path}
            className={classes.tab}
            component={LinkComponent}
            value={tab.activePath}
            underline="none"
            onClick={tab.onClick}
            sx={
              fullWidth
                ? { maxWidth: 'none', minWidth: 'fit-content', flex: 1 }
                : undefined
            }
            {...tab.props}
          />
        );
      })}
    </Tabs>
  );
};
